// StatPairBars — two teams × two stat bars (Phase 3).
//
// The shared primitive for the stat-pair type (Scored & Conceded, Penalty
// Record): each team gets a green "good" bar + a red "bad" bar, all four scaled
// to `max`. Extracted as both widgets share the exact DOM (they differ only in
// data + labels, resolved by the caller via @bento/shared shapers).
//
// `.sc-bar` has CSS `width: 0%` and NO width transition, so — like RankedBars —
// React owns the shell (labels + value text) and **D3 owns the bar widths** in a
// useEffect (a verbatim port of the legacy d3 width transition, so bars animate
// 0→value on first render and current→value on a control change). The shell is
// keyed by fixture so a fixture change remounts it (bars reset to CSS 0%, then
// animate up — matching the legacy shell rebuild). Emits the same DOM + classes
// as the legacy renderers (verified by the A/B).

import { useEffect, useRef } from "react";
import { select } from "d3-selection";
import "d3-transition";
import { easeCubicInOut } from "d3-ease";
import { statPairPct, type StatPairStat, type StatPairSide } from "@bento/shared";

const DURATION = 300;

export interface StatPairBarsProps {
  /** Keys the shell so a fixture change remounts it (bars reset to 0%). */
  fixture: string;
  first: StatPairStat;
  second: StatPairStat;
  home: StatPairSide;
  away: StatPairSide;
  max: number;
}

export default function StatPairBars({
  fixture,
  first,
  second,
  home,
  away,
  max,
}: StatPairBarsProps) {
  const wrapRef = useRef<HTMLDivElement>(null);

  // Bar values in document order: home-first, home-second, away-first, away-second.
  const values = [home.first, home.second, away.first, away.second];
  const sig = `${fixture}|${max}|${values.join(",")}`;

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const bars = wrap.querySelectorAll<HTMLElement>(".sc-bar");
    bars.forEach((bar, i) => {
      select(bar)
        .transition()
        .duration(DURATION)
        .ease(easeCubicInOut)
        .style("width", statPairPct(values[i] ?? 0, max) + "%");
    });
    // `sig` covers fixture + max + all four values — the complete dependency.
  }, [sig]);

  const block = (side: StatPairSide) => (
    <div className="sc-block" data-team={side === home ? "home" : "away"} key={`${fixture}-${side.team}`}>
      <div className="sc-team-label">{side.team}</div>
      <div className="sc-stat" data-stat={first.key}>
        <div className="sc-stat-header">
          <span className="sc-stat-name">{first.label}</span>
          <span className="sc-stat-val">{side.firstText}</span>
        </div>
        <div className="sc-track">
          <div className="sc-bar sc-scored bento-fill" />
        </div>
      </div>
      <div className="sc-stat" data-stat={second.key}>
        <div className="sc-stat-header">
          <span className="sc-stat-name">{second.label}</span>
          <span className="sc-stat-val">{side.secondText}</span>
        </div>
        <div className="sc-track">
          <div className="sc-bar sc-conceded bento-fill" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="data-wrapper top-align" ref={wrapRef}>
      {block(home)}
      {block(away)}
    </div>
  );
}
